/**
 * StaffManagementService
 * 직원 관리 비즈니스 로직
 */
import type { Staff, StaffRole, StaffStats } from '../models/StaffManagement';
import { formatDateKST } from '../../../utils/dateUtils';

/**
 * StaffManagementService 클래스
 * 직원 관련 비즈니스 로직 및 유틸리티 함수
 */
export class StaffManagementService {
  /**
   * 역할별 한글 라벨 매핑
   */
  private readonly roleLabels: Record<StaffRole, string> = {
    owner: '원장',
    manager: '매니저',
    staff: '정직원',
    parttime: '파트타임',
    facility: '시설관리',
  };

  /**
   * 역할별 색상 클래스 매핑
   */
  private readonly roleColors: Record<StaffRole, string> = {
    owner: 'bg-indigo-100 text-indigo-800',
    manager: 'bg-purple-100 text-purple-800',
    staff: 'bg-blue-100 text-blue-800',
    parttime: 'bg-green-100 text-green-800',
    facility: 'bg-yellow-100 text-yellow-800',
  };

  /**
   * 역할 한글명 반환
   */
  getRoleLabel(role: StaffRole): string {
    return this.roleLabels[role] || '알 수 없음';
  }

  /**
   * 역할 배지 색상 클래스 반환
   */
  getRoleColorClass(role: StaffRole): string {
    return this.roleColors[role] || 'bg-gray-100 text-gray-800';
  }

  /**
   * 활성화 상태 배지 클래스 반환
   */
  getActiveStatusClass(isActive: boolean): string {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  }

  /**
   * 활성화 상태 라벨 반환
   */
  getActiveStatusLabel(isActive: boolean): string {
    return isActive ? '활성' : '비활성';
  }

  /**
   * 직원 통계 계산
   */
  calculateStats(staffList: Staff[]): StaffStats {
    return {
      total: staffList.length,
      active: staffList.filter((s) => s.isActive).length,
      managers: staffList.filter((s) => s.role === 'manager').length,
      parttime: staffList.filter((s) => s.role === 'parttime').length,
    };
  }

  /**
   * 직원 필터링 (검색어)
   */
  filterBySearchTerm(staffList: Staff[], searchTerm: string): Staff[] {
    if (!searchTerm) return staffList;

    const lowerSearch = searchTerm.toLowerCase();
    return staffList.filter(
      (staff) =>
        staff.name.toLowerCase().includes(lowerSearch) ||
        staff.email.toLowerCase().includes(lowerSearch)
    );
  }

  /**
   * 직원 필터링 (역할)
   */
  filterByRole(staffList: Staff[], role?: StaffRole | 'all'): Staff[] {
    if (!role || role === 'all') return staffList;
    return staffList.filter((staff) => staff.role === role);
  }

  /**
   * 직원 필터링 (활성화 상태)
   */
  filterByActiveStatus(staffList: Staff[], isActive?: boolean): Staff[] {
    if (isActive === undefined) return staffList;
    return staffList.filter((staff) => staff.isActive === isActive);
  }

  /**
   * 복합 필터링
   */
  applyFilters(
    staffList: Staff[],
    searchTerm?: string,
    role?: StaffRole | 'all',
    isActive?: boolean
  ): Staff[] {
    let filtered = staffList;

    if (searchTerm) {
      filtered = this.filterBySearchTerm(filtered, searchTerm);
    }

    if (role) {
      filtered = this.filterByRole(filtered, role);
    }

    if (isActive !== undefined) {
      filtered = this.filterByActiveStatus(filtered, isActive);
    }

    return filtered;
  }

  /**
   * 날짜 포맷팅 (YYYY-MM-DD)
   */
  formatDate(dateString?: string): string {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      return formatDateKST(date);
    } catch {
      return dateString;
    }
  }

  /**
   * 날짜+시간 포맷팅 (YYYY-MM-DD HH:MM)
   */
  formatDateTime(dateString?: string): string {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      const dateStr = formatDateKST(date);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${dateStr} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  }

  /**
   * 이메일 유효성 검사
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * 전화번호 포맷팅 (010-1234-5678)
   */
  formatPhoneNumber(phone: string): string {
    // 숫자만 추출
    const cleaned = phone.replace(/\D/g, '');

    // 010-1234-5678 형식으로 변환
    if (cleaned.length === 11) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
    } else if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }

    return phone;
  }

  /**
   * 역할별 권한 설명 반환
   */
  getRolePermissionDescription(role: StaffRole): string {
    const descriptions: Record<StaffRole, string> = {
      owner: '계정 주인(원장) — 전체 권한',
      manager: '일상 운영 총괄, 회원 관리, 매출 조회, 공지사항 작성',
      staff: '체크인/아웃 처리, 좌석 관리, 회원 조회',
      parttime: '기본 체크인/아웃, 좌석 상태 확인',
      facility: '좌석 상태 확인, 시설 점검 기록',
    };

    return descriptions[role] || '권한 정보 없음';
  }
}

/**
 * StaffManagementService 싱글톤 인스턴스
 */
export const staffManagementService = new StaffManagementService();
